import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  Container,
  Chip,
} from "@mui/material";
import { useSearchParams, useNavigate } from "react-router-dom";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import VerifiedIcon from "@mui/icons-material/Verified";
import SendIcon from "@mui/icons-material/Send";
import { verifyEmail, resendCode } from "../../../Services/apiService";

const VerifiPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // read email and id from query string: ?email=...&id=...
  const emailFromQuery = searchParams.get("email") || "";
  const userIdFromQuery = searchParams.get("id") || "";

  const [email] = useState(emailFromQuery);
  const [userId] = useState(userIdFromQuery);

  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });

  useEffect(() => {
    if (userIdFromQuery) setCode(userIdFromQuery);
  }, [userIdFromQuery]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!userId) {
      setSnackbar({ open: true, severity: "error", message: "الرجاء توفير معرف المستخدم (id)." });
      return;
    }
    if (!code || !code.trim()) {
      setSnackbar({ open: true, severity: "error", message: "الرجاء إدخال كود التحقق." });
      return;
    }
    setLoading(true);
    try {
      const res = await verifyEmail(userId, code.trim());
      setSnackbar({ open: true, severity: "success", message: res?.data?.message || "تم التحقق بنجاح! 🎉" });
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      console.error("verify error:", err);
      const msg = err?.response?.data?.message || "فشل التحقق. تأكد من الكود وحاول مرة أخرى.";
      setSnackbar({ open: true, severity: "error", message: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      setSnackbar({ open: true, severity: "error", message: "لا يوجد بريد لإعادة الإرسال." });
      return;
    }
    setResendLoading(true);
    try {
      const res = await resendCode(email);
      setSnackbar({ open: true, severity: "success", message: res?.data?.message || "تم إرسال الكود إلى بريدك. 📧" });
    } catch (err) {
      console.error("resend error:", err);
      const msg = err?.response?.data?.message || "فشل إرسال الكود. حاول لاحقاً.";
      setSnackbar({ open: true, severity: "error", message: msg });
    } finally {
      setResendLoading(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%)",
        position: "relative",
        py: 6,
        px: 2,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage:
            "radial-gradient(circle, rgba(243, 111, 33, 0.03) 1px, transparent 1px)",
          backgroundSize: "30px 30px",
          pointerEvents: "none",
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, md: 6 },
            borderRadius: 5,
            border: "2px solid #e8e8e8",
            background: "white",
            boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
            transition: "all 0.3s ease",
            "&:hover": {
              boxShadow: "0 12px 48px rgba(243, 111, 33, 0.12)",
              borderColor: "#f36f21",
            },
          }}
        >
          {/* Icon Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                background: "linear-gradient(135deg, rgba(243, 111, 33, 0.1) 0%, rgba(230, 81, 0, 0.1) 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                animation: "pulse 2s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%, 100%": { transform: "scale(1)" },
                  "50%": { transform: "scale(1.05)" },
                },
              }}
            >
              <MarkEmailReadIcon sx={{ fontSize: 40, color: "#f36f21" }} />
            </Box>
          </Box>

          <Typography
            variant="h4"
            fontWeight={900}
            align="center"
            gutterBottom
            sx={{
              background: "linear-gradient(135deg, #f36f21 0%, #e65100 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              mb: 2,
            }}
          >
            تحقق من بريدك الإلكتروني
          </Typography>

          <Typography
            variant="body1"
            align="center"
            sx={{
              mb: 4,
              color: "text.secondary",
              lineHeight: 1.8,
              fontSize: "1.05rem",
            }}
          >
            أدخل كود التحقق المرسل إلى بريدك الإلكتروني أدناه
          </Typography>

          {email ? (
            <Chip
              icon={<MarkEmailReadIcon />}
              label={email}
              sx={{
                mb: 4,
                width: "100%",
                height: "auto",
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
                background: "linear-gradient(135deg, rgba(243, 111, 33, 0.08) 0%, rgba(230, 81, 0, 0.08) 100%)",
                color: "#f36f21",
                border: "2px solid rgba(243, 111, 33, 0.2)",
                "& .MuiChip-icon": {
                  color: "#f36f21",
                },
              }}
            />
          ) : (
            <Alert severity="warning" sx={{ mb: 4, borderRadius: 3 }}>
              ملاحظة: لم يتم تمرير البريد الإلكتروني في الرابط.
            </Alert>
          )}

          <Box component="form" onSubmit={handleVerify} sx={{ display: "grid", gap: 3 }}>
            <TextField
              label="كود التحقق"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              fullWidth
              autoFocus
              placeholder="أدخل الكود المكون من 6 أرقام"
              InputProps={{
                sx: {
                  fontSize: "1.2rem",
                  textAlign: "center",
                  letterSpacing: "0.5rem",
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  "&:hover fieldset": {
                    borderColor: "#f36f21",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#f36f21",
                    borderWidth: 2,
                  },
                },
              }}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <VerifiedIcon />}
              sx={{
                py: 2,
                fontSize: "1.1rem",
                fontWeight: 700,
                borderRadius: 3,
                textTransform: "none",
                background: "linear-gradient(135deg, #f36f21 0%, #e65100 100%)",
                boxShadow: "0 6px 20px rgba(243, 111, 33, 0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #e65100 0%, #d84315 100%)",
                  boxShadow: "0 8px 28px rgba(243, 111, 33, 0.4)",
                  transform: "translateY(-2px)",
                },
                "&:disabled": {
                  background: "rgba(243, 111, 33, 0.5)",
                },
              }}
            >
              {loading ? "جاري التحقق..." : "تحقق من الحساب"}
            </Button>

            <Button
              type="button"
              variant="outlined"
              size="large"
              disabled={resendLoading}
              onClick={handleResend}
              startIcon={resendLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              sx={{
                py: 1.8,
                fontSize: "1rem",
                fontWeight: 600,
                borderRadius: 3,
                textTransform: "none",
                borderWidth: 2,
                borderColor: "#f36f21",
                color: "#f36f21",
                "&:hover": {
                  borderWidth: 2,
                  borderColor: "#e65100",
                  background: "rgba(243, 111, 33, 0.05)",
                  transform: "translateY(-2px)",
                },
                "&:disabled": {
                  borderColor: "rgba(243, 111, 33, 0.3)",
                  color: "rgba(243, 111, 33, 0.5)",
                },
              }}
            >
              {resendLoading ? "جارٍ الإرسال..." : "إعادة إرسال الكود"}
            </Button>
          </Box>

          <Box
            sx={{
              mt: 4,
              p: 3,
              borderRadius: 3,
              background: "linear-gradient(135deg, rgba(243, 111, 33, 0.05) 0%, rgba(230, 81, 0, 0.05) 100%)",
              border: "1px solid rgba(243, 111, 33, 0.15)",
            }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.8 }}>
              💡 <strong>نصيحة:</strong> تحقق من مجلد الرسائل غير المرغوب فيها (Spam) إذا لم تجد الرسالة في صندوق الوارد.
            </Typography>
          </Box>
        </Paper>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            borderRadius: 3,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            fontSize: "1rem",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VerifiPage;
